// Placement Queue — cross-deal WIP list. Every deal in submitted /
// quoting / bound status with its capacity progress bar. Click a row
// to open the per-deal Placement Board.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink: '#1e293b', muted: '#64748b', border: '#e2e8f0', soft: '#f8fafc', burgundy: '#800020',
  green: '#059669', blue: '#0284c7', amber: '#d97706', grey: '#94a3b8', red: '#dc2626',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '28px 40px', minHeight: 0 },
  h1:      { fontSize: 24, fontWeight: 700, color: C.ink, margin: '0 0 4px' },
  sub:     { color: C.muted, margin: '0 0 20px', fontSize: 13, maxWidth: 720 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center' },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },

  table:   { width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', fontSize: 13 },
  th:      { textAlign: 'left', padding: '12px 14px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  td:      { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: C.ink, verticalAlign: 'middle' },

  bar:     { position: 'relative', height: 12, borderRadius: 4, background: C.soft, overflow: 'hidden', border: `1px solid ${C.border}` },
  seg:     (color, w, left) => ({ position: 'absolute', top: 0, bottom: 0, background: color, width: `${w}%`, left: `${left}%` }),
  pctText: { fontSize: 11, color: C.muted, marginTop: 4 },
  flagPill:(tone) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
    background: tone === 'over' ? '#fee2e2' : tone === 'full' ? '#dcfce7' : '#fef3c7',
    color:      tone === 'over' ? '#991b1b' : tone === 'full' ? '#166534' : '#92400e',
  }),
  empty:   { padding: '48px 24px', textAlign: 'center', color: C.muted },
  err:     { color: C.red, fontSize: 13 },
};

const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toISOString().slice(0,10); } catch (e) { return String(v).slice(0,10); } };

const PlacementQueue = () => {
  const history = useHistory();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/placement/queue`, { withCredentials: true });
      setRows(resp?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load placement queue');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AppLayout>
      <div style={S.page}>
        <h1 style={S.h1}>Placement Queue</h1>
        <p style={S.sub}>Deals still in placement — submitted, quoting, or bound but not yet fully closed. Green segment is signed capacity; blue firm order; amber quoted; grey approached. Marker at 100% is the cession target.</p>

        <div style={S.toolbar}>
          <button style={S.ghost} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          <div style={{ flex: 1 }} />
          {error && <div style={S.err}>{error}</div>}
        </div>

        {!loading && rows.length === 0 ? (
          <div style={{ ...S.table }}>
            <div style={S.empty}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>No deals in placement</div>
              <div style={{ marginTop: 6 }}>Deals in draft or closed status don't appear here.</div>
            </div>
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Deal #</th>
                <th style={S.th}>Cedant / Insured</th>
                <th style={S.th}>Line / Structure</th>
                <th style={S.th}>Period</th>
                <th style={S.th}>Status</th>
                <th style={S.th} colSpan={2}>Capacity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cap = r.capacity || {};
                const t = cap.totals || {};
                const flags = cap.flags || {};
                const meterMax = Math.max(100, (t.signed || 0) + (t.firm || 0) + (t.quoted || 0) + (t.approached || 0));
                const seg = (n) => Math.max(0, Math.min(100, ((n || 0) / meterMax) * 100));
                const tone = flags.overPlaced ? 'over' : flags.fullyPlaced ? 'full' : 'under';
                return (
                  <tr
                    key={r._id}
                    onClick={() => history.push(`/reinsurance/deals/${r._id}/placement`)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.soft; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...S.td, fontWeight: 600, color: C.burgundy }}>{r.dealNumber}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{r.cedantName}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{r.insuredName || ''}</div>
                    </td>
                    <td style={S.td}>{[r.lineOfBusiness, r.structureType].filter(Boolean).join(' / ') || '—'}</td>
                    <td style={S.td}>{fmtDate(r.periodStart)} to {fmtDate(r.periodEnd)}</td>
                    <td style={S.td}>{r.status}</td>
                    <td style={{ ...S.td, minWidth: 240 }}>
                      <div style={S.bar}>
                        <div style={S.seg(C.green, seg(t.signed),     0)} />
                        <div style={S.seg(C.blue,  seg(t.firm),       seg(t.signed))} />
                        <div style={S.seg(C.amber, seg(t.quoted),     seg(t.signed) + seg(t.firm))} />
                        <div style={S.seg(C.grey,  seg(t.approached), seg(t.signed) + seg(t.firm) + seg(t.quoted))} />
                        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${(100 / meterMax) * 100}%`, width: 2, background: '#000', opacity: 0.4 }} />
                      </div>
                      <div style={S.pctText}>
                        Signed <b>{t.signed || 0}%</b> · Firm {t.firm || 0}% · Quoted {t.quoted || 0}% · Unsigned {t.unsigned ?? t.unplaced ?? 0}% · Open {t.open ?? 0}%
                      </div>
                    </td>
                    <td style={S.td}>
                      <span style={S.flagPill(tone)}>{flags.overPlaced ? 'over' : flags.fullyPlaced ? 'full' : 'under'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
};

export default PlacementQueue;
